import { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin, Clock, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import TrustpilotSection from "../components/TrustpilotSection";
import { useMedia } from "../hooks/useMedia";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { url } = useMedia();
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_type: "office", size: "medium", service: "", message: "", property_address: "", property_city: "", property_state: "NY", property_zip: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Ambient light — follows mouse across the form panel
  const formRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const smx = useSpring(mx, { stiffness: 100, damping: 22 });
  const smy = useSpring(my, { stiffness: 100, damping: 22 });
  const lightX = useTransform(smx, [0, 1], ["0%", "100%"]);
  const lightY = useTransform(smy, [0, 1], ["0%", "100%"]);

  function onFormMove(e) {
    const el = formRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
    my.set(Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)));
  }

  function change(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || "General enquiry",
        property_type: form.property_type,
        property_size: form.size,
        property_address: form.property_address,
        property_city: form.property_city,
        property_state: form.property_state,
        property_zip: form.property_zip,
        message: form.message,
        source: "contact-page",
      });
      setSent(true);
      toast.success("Message received. We'll respond within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try phone or email.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title={<>Let's <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>talk.</span></>}
        subtitle="Every message is read by a founder or account lead — never a call centre. We respond within one business hour."
        image={url("about-hero") || url("outdoor-lighting")}
        kicker="Two markets · one Certificate of Insurance · one number."
      />

      {/* Verified reviews — social proof between hero and form */}
      <TrustpilotSection compact />

      <section className="py-24 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 grid lg:grid-cols-12 gap-14">
          {/* Left — direct channels */}
          <div className="lg:col-span-4 space-y-8">
            <div className="overline">Direct Channels</div>
            <div className="glass-card p-7 flex items-start gap-5" data-testid="contact-phone" data-cursor="hover">
              <Phone size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>Toll-Free</div>
                <a href="tel:+18338237765" className="mt-2 font-display text-2xl block hover:text-white transition-colors" style={{ letterSpacing: "-0.02em" }}>(833) 8BESPOKE</a>
                <div className="mt-1 text-xs font-mono" style={{ color: "var(--text-3)" }}>(833) 823-7765</div>
                <div className="mt-3 flex flex-col gap-1 text-sm font-body" style={{ color: "var(--text-2)" }}>
                  <a href="tel:+15182472467" className="hover:text-white">Local · (518) 247-2467</a>
                  <a href="tel:+17188780551" className="hover:text-white">Direct · (718) 878-0551</a>
                </div>
              </div>
            </div>
            <a href="mailto:hello@bespokeexteriorsolutions.com" className="glass-card p-7 flex items-start gap-5 block" data-testid="contact-email" data-cursor="hover">
              <Mail size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>Email</div>
                <div className="mt-2 font-display text-xl break-all" style={{ letterSpacing: "-0.02em" }}>hello@bespokeexteriorsolutions.com</div>
                <div className="mt-2 text-sm font-body" style={{ color: "var(--text-2)" }}>Every message read by a founder</div>
              </div>
            </a>
            <a
              href="https://maps.google.com/?q=185+Great+Neck+Rd+4th+Floor+Great+Neck+NY+11021"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-7 flex items-start gap-5 block"
              data-testid="contact-address"
              data-cursor="hover"
            >
              <MapPin size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>Headquarters</div>
                <div className="mt-2 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>185 Great Neck Rd, 4th Floor</div>
                <div className="mt-1 font-display text-lg" style={{ letterSpacing: "-0.02em", color: "var(--text-2)" }}>Great Neck, NY 11021</div>
                <div className="mt-3 text-xs font-mono uppercase tracking-[0.3em] flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
                  Open in Google Maps <ArrowUpRight size={11} />
                </div>
                <div className="mt-3 text-sm font-body" style={{ color: "var(--text-2)" }}>Service: NYC · Long Island · Charlotte NC.</div>
              </div>
            </a>
            <div className="glass-card p-7 flex items-start gap-5">
              <Clock size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>Response SLA</div>
                <div className="mt-2 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>Under 1 hour · Business hours</div>
                <div className="mt-2 text-sm font-body" style={{ color: "var(--text-2)" }}>24-hour turnaround on quotes.</div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-8">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9 }}
                className="glass-card p-14 text-center"
                data-testid="contact-success"
              >
                <div className="w-16 h-16 mx-auto flex items-center justify-center" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--gold)", background: "rgba(198,168,124,0.14)" }}>
                  <Check size={20} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
                </div>
                <h2 className="mt-8 font-display text-4xl md:text-5xl" style={{ letterSpacing: "-0.03em" }}>
                  Received.
                </h2>
                <p className="mt-6 font-body max-w-md mx-auto" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
                  Your message is in front of an account lead. Expect a reply within one business hour.
                </p>
                <Link to="/pricing" className="btn-glow mt-10 inline-flex" data-cursor="hover" data-testid="contact-cta-plans">
                  See Instant Plans <ArrowUpRight size={13} />
                </Link>
              </motion.div>
            ) : (
              <form
                ref={formRef}
                onSubmit={submit}
                onMouseMove={onFormMove}
                className="glass-card glass-reflect p-10 md:p-14 grid md:grid-cols-2 gap-6 relative overflow-hidden"
                data-testid="contact-form"
                style={{
                  background: "linear-gradient(160deg, rgba(20,20,24,0.72), rgba(10,10,13,0.85))",
                  backdropFilter: "blur(24px)",
                  WebkitBackdropFilter: "blur(24px)",
                  border: "1px solid var(--line-gold)",
                }}
              >
                {/* Ambient mouse-follow light */}
                <motion.div
                  aria-hidden
                  className="absolute pointer-events-none"
                  style={{
                    left: lightX, top: lightY,
                    width: 520, height: 520,
                    transform: "translate(-50%, -50%)",
                    background: "radial-gradient(circle, rgba(198,168,124,0.16), rgba(198,168,124,0.04) 40%, transparent 65%)",
                    filter: "blur(30px)",
                    mixBlendMode: "screen",
                    zIndex: 0,
                  }}
                />
                {/* Content */}
                <div className="md:col-span-2 overline mb-2 relative">Send A Message</div>
                {[
                  { k: "name",  label: "Full Name",     type: "text",  required: true,  span: 1 },
                  { k: "email", label: "Business Email", type: "email", required: true,  span: 1 },
                  { k: "phone", label: "Phone",          type: "tel",   required: false, span: 1 },
                  { k: "service", label: "Interested Service", type: "text", required: false, span: 1, placeholder: "Window cleaning, pressure washing, ..." },
                ].map((f) => (
                  <div key={f.k} className={`md:col-span-${f.span}`}>
                    <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>{f.label}</label>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      placeholder={f.placeholder || ""}
                      onChange={(e) => change(f.k, e.target.value)}
                      data-testid={`contact-${f.k}`}
                      className="w-full bg-transparent px-5 py-4 font-body transition-colors focus:outline-none"
                      style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }}
                    />
                  </div>
                ))}
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>Property Type</label>
                  <select value={form.property_type} onChange={(e) => change("property_type", e.target.value)} data-testid="contact-property-type"
                    className="w-full bg-transparent px-5 py-4 font-body" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }}>
                    {["Restaurant", "Retail", "Office", "Medical", "Hotel", "Apartments", "Property Manager", "Industrial", "Government", "School", "Other"].map((v) => <option key={v} value={v} style={{ background: "#0b0b0d" }}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>Property Size</label>
                  <select value={form.size} onChange={(e) => change("size", e.target.value)} data-testid="contact-size"
                    className="w-full bg-transparent px-5 py-4 font-body" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }}>
                    <option value="small"  style={{ background: "#0b0b0d" }}>Small · &lt; 1,500 sq ft</option>
                    <option value="medium" style={{ background: "#0b0b0d" }}>Medium · 1,500–4,000 sq ft</option>
                    <option value="large"  style={{ background: "#0b0b0d" }}>Large · 4,000+ sq ft</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>Property Street Address</label>
                  <input type="text" value={form.property_address} onChange={(e) => change("property_address", e.target.value)} data-testid="contact-property-address" placeholder="e.g. 145 Broadway"
                    className="w-full bg-transparent px-5 py-4 font-body focus:outline-none" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>City</label>
                  <input type="text" value={form.property_city} onChange={(e) => change("property_city", e.target.value)} data-testid="contact-property-city" placeholder="New York"
                    className="w-full bg-transparent px-5 py-4 font-body focus:outline-none" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>State</label>
                    <input type="text" value={form.property_state} onChange={(e) => change("property_state", e.target.value)} data-testid="contact-property-state" placeholder="NY" maxLength={2}
                      className="w-full bg-transparent px-5 py-4 font-body focus:outline-none uppercase" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>Zip</label>
                    <input type="text" value={form.property_zip} onChange={(e) => change("property_zip", e.target.value)} data-testid="contact-property-zip" placeholder="10001" maxLength={10}
                      className="w-full bg-transparent px-5 py-4 font-body focus:outline-none" style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }} />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "var(--text-3)" }}>Message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => change("message", e.target.value)}
                    rows={5}
                    data-testid="contact-message"
                    placeholder="Tell us about your property and what you're looking to solve."
                    className="w-full bg-transparent px-5 py-4 font-body"
                    style={{ borderRadius: "var(--r-md)", border: "1px solid var(--line)", color: "var(--text)" }}
                  />
                </div>
                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-5 pt-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.36em] max-w-md" style={{ color: "var(--text-3)" }}>
                    Every message is read by a founder or account lead. Response within one business hour.
                  </p>
                  <button type="submit" disabled={sending} className="btn-glow" data-testid="contact-submit" data-cursor="hover">
                    {sending ? "Sending…" : "Send Message"} <ArrowUpRight size={13} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
